import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { DropdownButtonDirection } from "@/DesignSystem/Molecules/DropdownButton";

interface IPortalProps {
    /**
     * The reference of the element of reference for the portal.
     */
    targetRef: React.RefObject<HTMLElement>;

    /**
     * The elements inside the portal.
     */
    children: React.ReactNode;

    /**
     * Preferred portal direction relative to the target.
     * bottomRight | bottomLeft | topRight | topLeft
     */
    direction?: DropdownButtonDirection;

    /**
     * The minimum width the portal must have.
     */
    minWidth?: number;
}

function Portal({
    targetRef,
    children,
    direction = "bottomRight",
    minWidth
}: Readonly<IPortalProps>): JSX.Element {
    const [position, setPosition] = useState({ left: 0, top: 0 });
    const portalContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (targetRef.current && portalContainerRef.current) {
            const targetRect = targetRef.current.getBoundingClientRect();
            const portalHeight = portalContainerRef.current.offsetHeight;
            const portalWidth = portalContainerRef.current.offsetWidth;

            const preferTop = direction.startsWith("top");
            const preferRight = direction.endsWith("Right");

            // Default position based on preferred direction
            let newPosition = {
                left: preferRight
                    ? targetRect.right + window.scrollX - portalWidth // Right edge of portal aligns with right edge of target
                    : targetRect.left + window.scrollX, // Left edge of portal aligns with left edge of target
                top: preferTop
                    ? targetRect.top + window.scrollY - portalHeight // Top edge of portal aligns with top edge of target
                    : targetRect.bottom + window.scrollY // Top edge of portal aligns with bottom edge of target
            };

            // Calculate available space around the target
            const spaceBelow = window.innerHeight - targetRect.bottom;
            const spaceAbove = targetRect.top;
            const spaceRight = window.innerWidth - targetRect.right;
            const spaceLeft = targetRect.left;

            // Vertical placement: check available space and position accordingly
            if (preferTop) {
                // Prefer top placement (above the target)
                if (spaceAbove >= portalHeight) {
                    // Enough space above -> place above
                    newPosition.top = targetRect.top + window.scrollY - portalHeight;
                }
                else if (spaceBelow >= portalHeight) {
                    // Not enough above, but enough below -> place below
                    newPosition.top = targetRect.bottom + window.scrollY;
                }
                else {
                    // Not enough space on either side -> place above (may go off-screen)
                    newPosition.top = targetRect.top + window.scrollY - portalHeight;
                }
            }
            else {
                // Prefer bottom placement (below the target)
                if (spaceBelow >= portalHeight) {
                    // Enough space below -> place below
                    newPosition.top = targetRect.bottom + window.scrollY;
                }
                else if (spaceAbove >= portalHeight) {
                    // Not enough below, but enough above -> place above
                    newPosition.top = targetRect.top + window.scrollY - portalHeight;
                }
                else {
                    // Not enough space on either side -> place below (may go off-screen)
                    newPosition.top = targetRect.bottom + window.scrollY;
                }
            }

            // Horizontal placement: check available space and position accordingly
            if (preferRight) {
                newPosition.left = targetRect.right + window.scrollX - portalWidth;
            }
            else {
                if (spaceLeft >= portalWidth) {
                    // Enough space on the left -> place left-aligned
                    newPosition.left = targetRect.left + window.scrollX;
                }
                else if (spaceRight >= portalWidth) {
                    // Not enough on left, but enough on right -> flip to right-aligned
                    newPosition.left = targetRect.right + window.scrollX - portalWidth;
                }
                else {
                    // Not enough space on either side -> stay left-aligned (may go off-screen)
                    newPosition.left = targetRect.left + window.scrollX;
                }
            }

            // Only prevent completely off-screen positioning (minimal bounds check)
            if (newPosition.left < 0) {
                newPosition.left = 0;
            }

            // Update the position state
            setPosition(newPosition);
        }
    }, [targetRef, direction, children]);

    return createPortal(
        <div
            ref={portalContainerRef}
            style={{
                left: `${position.left}px`,
                minWidth: `${minWidth}px`,
                position: "absolute",
                top: `${position.top}px`,
                zIndex: "999"
            }}
        >
            {children}
        </div>,
        document.body
    );
}

export { Portal };
